<template>
  <aside
    class="spm-sidebar"
    :class="{ 'is-expanded': isSidebarExpanded }"
  >
    <div class="spm-sidebar__logo">
      <router-link
        :to="{ name: 'home' }"
        class="spm-sidebar__logo-link"
      >
        <ImageWithFallback
          v-if="isSidebarExpanded"
          class="spm-sidebar__logo-img--full"
          :placeholder="defaultLogo"
          :sources="UserState.logos"
        />
        <ImageWithFallback
          v-else
          class="spm-sidebar__logo-img--mini"
          :placeholder="defaultMinLogo"
          :sources="defaultMinLogo"
        />
      </router-link>
    </div>

    <nav class="spm-sidebar__nav">
      <div class="spm-sidebar__section">
        <span class="spm-sidebar__section-label">Menu</span>
      </div>
      <div class="spm-sidebar__menu">
        <SpmPanelMenu
          v-model:expanded-keys="expandedKeys"
          :items="items"
          context="sidebar"
          @update:expanded-keys="expandedKeysUpdated"
        />
      </div>
    </nav>
  </aside>
  <Menu
    id="menuProducts"
    ref="menuProducts"
    :model="itemsProducts"
    :popup="true"
  />
  <Menu
    id="menuLists"
    ref="menuLists"
    :model="itemsLists"
    :popup="true"
  />
  <Menu
    id="menuContentData"
    ref="menuContentData"
    :model="itemsContentData"
    :popup="true"
  />
  <Menu
    id="menuStats"
    ref="menuStats"
    :model="itemsStats"
    :popup="true"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onBeforeMount, Ref, ref, watch,
} from 'vue';
import { useStore } from '@/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import defaultLogo from '@/assets/images/logo-tc-black.svg';
import defaultMinLogo from '@/assets/images/logo.png';
import { findIfNoPermissionsGiven, findPermission, UserState } from '@/composables/User';
import ImageWithFallback from '@/components/ImageWithFallback.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';
import { getUserStatsPagesV2 } from '@/composables/Statistics/usersPagesSubscriptions';
import { StatsType } from '@/types';
import Menu from 'primevue/menu';

export default defineComponent({
  name: 'AppSidebar',

  components: {
    SpmPanelMenu,
    ImageWithFallback,
    Menu,
  },

  props: {
    isMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup(props) {
    const { t, locale } = useI18n();
    const currentRoute = computed(() => useRoute());
    const isMobile = ref(false);
    // eslint-disable-next-line no-restricted-globals
    isMobile.value = screen.width < 768;

    const expandedKeys: Ref<any> = ref({
      1: true,
      2: true,
      3: true,
      4: true,
    });
    const store = useStore();
    let shouldSidebarBeExpanded = false;
    if (!props.isMobile) {
      shouldSidebarBeExpanded = Boolean(Number(getLocalStorageElement('spm_is_sidebar_expanded') ?? 1));
    }
    store.commit('general/setIsSidebarExpanded', shouldSidebarBeExpanded);

    const isSidebarExpanded = computed(() => store.state.general.isSidebarExpanded);

    const isMenuItemActive = (routes: string | string[], param: string | null = null): string | null => {
      const activeClass = 'activeMenuItem';
      const currentRouteName = currentRoute.value.name;

      if (typeof routes === 'string') {
        if (param) {
          return currentRouteName === routes && Object.values(currentRoute.value.params).includes(param) ? activeClass : null;
        }
        return currentRouteName === routes ? activeClass : null;
      }
      if (routes.length) {
        for (let i = 0; i < routes.length; i++) {
          if (currentRouteName === routes[i]) {
            return activeClass;
          }
        }
      }
      return null;
    };

    const isAtLeastOneCampaignMenuVisible = findIfNoPermissionsGiven('automated_scenarios.view,bulk_campaigns.view');
    const isAtLeastOneListMenuVisible = findIfNoPermissionsGiven('lists-segments.mailing_lists_view,my_push_notif.view,my_unsubscribed.view,'
      + 'my_bounces.view,my_spams.view');
    const isAtLeastOneContentDataMenuVisible = findIfNoPermissionsGiven('my_templates.view,my_data.view,my_events.view,my_webhooks.view');

    const itemsProducts: Ref<any> = ref([
      {
        key: '1_0',
        label: t('navbar.autoScenarios'),
        to: { name: 'campaigns-automation-list' },
        class: computed(() => isMenuItemActive('campaigns-automation-list')),
        visible: () => isAtLeastOneCampaignMenuVisible && findPermission('automated_scenarios.view'),
      },
      {
        key: '1_1',
        label: t('navbar.campaignsBulk'),
        to: { name: 'campaigns-bulk-list' },
        class: computed(() => isMenuItemActive('campaigns-bulk-list')),
        visible: () => isAtLeastOneCampaignMenuVisible && findPermission('bulk_campaigns.view'),
      },
    ]);
    const itemsLists: Ref<any> = ref([
      {
        key: '2_0',
        label: t('navbar.mailingLists'),
        to: { name: 'lists-manage' },
        class: computed(() => isMenuItemActive('lists-manage')),
        visible: () => isAtLeastOneListMenuVisible && findPermission('lists-segments.mailing_lists_view'),
      },
      {
        key: '2_1',
        label: t('navbar.contacts'),
        to: { name: 'lists.contacts' },
        class: computed(() => isMenuItemActive('lists.contacts')),
        visible: () => isAtLeastOneListMenuVisible && findPermission('lists-segments.mailing_lists_view'),
      },
      {
        key: '2_2',
        label: t('navbar.consents'),
        to: { name: 'lists.consents' },
        class: computed(() => isMenuItemActive('lists.consents')),
        visible: () => isAtLeastOneListMenuVisible && findPermission('consents.view'),
      },
      {
        key: '2_3',
        label: t('navbar.rejects'),
        to: { name: 'lists.rejects' },
        class: computed(() => isMenuItemActive('lists.rejects')),
        visible: () => isAtLeastOneListMenuVisible && findPermission('rejets.view'),
      },
    ]);
    const itemsContentData: Ref<any> = ref([
      {
        key: '4_0',
        label: t('navbar.myTemplates'),
        to: { name: 'template-manage-v2' },
        class: computed(() => isMenuItemActive('template-manage-v2')),
        visible: () => isAtLeastOneContentDataMenuVisible && findPermission('my_templates.view'),
      },
      {
        key: '4_1',
        label: t('navbar.systemData'),
        to: { name: 'shop.system-objects' },
        class: computed(() => isMenuItemActive('shop.system-objects')),
        visible: () => isAtLeastOneContentDataMenuVisible && findPermission('my_data.view'),
      },
      {
        key: '4_2',
        label: t('navbar.customData'),
        to: { name: 'shop.objects-data-list' },
        class: computed(() => isMenuItemActive('shop.objects-data-list')),
        visible: () => isAtLeastOneContentDataMenuVisible && findPermission('my_data.view'),
      },
      {
        key: '4_3',
        label: t('customStats.list.title'),
        to: { name: 'custom-kpis.list' },
        class: computed(() => isMenuItemActive(['custom-kpis.list', 'custom-kpis.create', 'custom-kpis.edit'])),
        // Temporairement masqué du menu le temps de valider la feature en
        // prod. La route reste accessible via /custom-kpis (router intact).
        // Restaurer : `visible: () => findPermission('custom_stats.read')`.
        visible: () => false,
      },
      // {
      //   key: '4_2',
      //   label: t('navbar.events'),
      //   to: { name: 'shop.events' },
      //   class: computed(() => isMenuItemActive('shop.events')),
      //   visible: () => isAtLeastOneContentDataMenuVisible && findPermission('my_data.view'),
      // },
      // {
      //   key: '4_3',
      //   label: t('navbar.webhooks'),
      //   to: { name: 'shop.webhooks' },
      //   class: computed(() => isMenuItemActive('shop.webhooks')),
      //   visible: () => isAtLeastOneContentDataMenuVisible && findPermission('my_data.view'),
      // },
    ]);
    const itemsStats: Ref<any> = ref([]);

    const topMenuCss = ref(0);
    const menuProducts = ref();
    const menuLists = ref();
    const menuContentData = ref();
    const menuStats = ref();

    const hideAllMenus = () => {
      menuProducts.value.hide();
      menuLists.value.hide();
      menuContentData.value.hide();
      menuStats.value.hide();
    };

    const showPopupMenu = (event: any, menuRef: any, menuId: string) => {
      event.stopPropagation();
      const target = event.originalTarget ?? event.target;
      hideAllMenus();
      menuRef.value.toggle(event);
      setTimeout(() => {
        const el = document.getElementById(menuId);
        if (el) {
          el.style.position = 'fixed';
          el.style.top = `${target.getBoundingClientRect().top}px`;
          el.style.left = '68px';
        }
      }, 0);
    };

    const items = ref([
      {
        key: '0',
        label: t('navbar.dashboard'),
        icon: 'fa-regular fa-house',
        to: { name: 'home' },
        class: computed(() => isMenuItemActive('home')),
      },
      {
        key: '1',
        label: t('navbar.products'),
        icon: 'fa-regular fa-share-nodes',
        items: computed(() => {
          if (!isSidebarExpanded.value) {
            return false;
          }
          return itemsProducts.value;
        }),
        class: computed(() => isMenuItemActive(['workflow', 'campaigns-bulk'])),
        isExpanded: computed(() => isMenuItemActive(['workflow', 'campaigns-bulk'])),
        commandRoot: (event: any) => {
          if (!isSidebarExpanded.value) {
            showPopupMenu(event, menuProducts, 'menuProducts');
          }
        },
        visible: () => isAtLeastOneCampaignMenuVisible,
      },
      {
        key: '2',
        label: t('navbar.lists'),
        icon: 'fa-regular fa-address-book',
        items: computed(() => {
          if (!isSidebarExpanded.value) {
            return false;
          }
          return itemsLists.value;
        }),
        class: computed(() => isMenuItemActive([
          'lists-manage',
          'lists.push-tokens',
          'lists.manage-customers-unsub',
          'lists.manage-customers-bounces',
          'lists.manage-customers-spams',
        ])),
        isExpanded: computed(() => isMenuItemActive(['workflow', 'campaigns-bulk'])),
        commandRoot: (event: any) => {
          if (!isSidebarExpanded.value) {
            showPopupMenu(event, menuLists, 'menuLists');
          }
        },
        visible: () => isAtLeastOneListMenuVisible,
      },
      {
        key: '3',
        label: t('navbar.contentData'),
        icon: 'fa-regular fa-database',
        items: computed(() => {
          if (!isSidebarExpanded.value) {
            return false;
          }
          return itemsContentData.value;
        }),
        class: computed(() => isMenuItemActive(['shop.data', 'template-manage', 'shop.system-objects', 'shop.objects-data-list', 'custom-kpis.list', 'custom-kpis.create', 'custom-kpis.edit'])),
        isExpanded: computed(() => isMenuItemActive(['shop.data', 'template-manage', 'shop.system-objects', 'shop.objects-data-list', 'custom-kpis.list', 'custom-kpis.create', 'custom-kpis.edit'])),
        commandRoot: (event: any) => {
          if (!isSidebarExpanded.value) {
            showPopupMenu(event, menuContentData, 'menuContentData');
          }
        },
        visible: () => isAtLeastOneContentDataMenuVisible,
      },
      {
        key: '4',
        label: t('navbar.statistics'),
        icon: 'fa-regular fa-chart-mixed',
        items: computed(() => {
          if (!isSidebarExpanded.value) {
            return false;
          }
          return itemsStats.value;
        }),
        isExpanded: computed(() => isMenuItemActive(['custom_stats'])),
        commandRoot: (event: any) => {
          if (!isSidebarExpanded.value) {
            showPopupMenu(event, menuStats, 'menuStats');
          }
        },
      },
    ]);

    const expandNode = (node: any) => {
      if (node.items && node.items.length) {
        expandedKeys.value[node.key] = true;

        // eslint-disable-next-line no-restricted-syntax
        for (const child of node.items) {
          expandNode(child);
        }
      }
    };

    const activeRightMenu = (): void => {
      if (currentRoute.value.name) {
        const routeName = currentRoute.value.name;
        items.value.forEach((item: any) => {
          if (item.to && item.to.name === routeName) {
            expandNode(items.value[parseInt(item.key.split('_')[0], 10)]);
          } else if (item.items) {
            item.items.forEach((subItem: any) => {
              if (subItem.to.name === routeName) {
                expandNode(items.value[parseInt(subItem.key.split('_')[0], 10)]);
              }
            });
          }
        });
      }
    };

    const setListOfUsersStatsPages = async () => {
      const result = await getUserStatsPagesV2(UserState.user.id, UserState.user.userType, StatsType.STATS, UserState.activeShop?.idUser);

      itemsStats.value = [];

      if (result && result.success && result.items && result.items.length) {
        result.items.forEach((route: any, index: number) => {
          let navigationName = route.page?.page_name;
          try {
            navigationName = JSON.parse(navigationName);
            if (locale.value in navigationName) {
              navigationName = navigationName[locale.value];
              console.log(navigationName[Object.keys(navigationName)[0]]);
            } else {
              // get the first key of the object
              navigationName = navigationName[Object.keys(navigationName)[0]];
            }
            itemsStats.value.push({
              key: `4_${index}`,
              label: navigationName,
              to: { name: 'custom_stats', params: { pageId: route.id_users_pages_configuration } },
              class: computed(() => isMenuItemActive('custom_stats', route.id_users_pages_configuration.toString())),
            });
          } catch (error) {
            console.log('error', error);
          }
        });
      }
    };

    onBeforeMount(async () => {
      store.commit('statsEditor/setRefreshNavigationBar', false);
      await setListOfUsersStatsPages();

      const expandedSidebarKeys = getLocalStorageElement('expandedSidebarKeys');
      if (expandedSidebarKeys) {
        expandedKeys.value = JSON.parse(expandedSidebarKeys);
      } else {
        setLocalStorageElement('expandedSidebarKeys', JSON.stringify(expandedKeys.value));
      }

      activeRightMenu();
    });

    const expandedKeysUpdated = (newExpandedKeys: any) => {
      setLocalStorageElement('expandedSidebarKeys', JSON.stringify(newExpandedKeys));
    };

    watch(currentRoute.value, async () => {
      activeRightMenu();
    }, { deep: true });

    watch(() => store.getters['statsEditor/getRefreshNavigationBar'], async () => {
      if (store.getters['statsEditor/getRefreshNavigationBar']) {
        await setListOfUsersStatsPages();
        store.commit('statsEditor/setRefreshNavigationBar', false);
        activeRightMenu();
      }
    });

    return {
      defaultLogo,
      UserState,
      isSidebarExpanded,
      items,
      defaultMinLogo,
      expandedKeys,
      expandedKeysUpdated,
      itemsProducts,
      menuProducts,
      itemsLists,
      menuLists,
      itemsContentData,
      menuContentData,
      itemsStats,
      menuStats,
    };
  },

});
</script>

<style lang="scss">
.spm-sidebar {
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif,
  BlinkMacSystemFont, Helvetica Neue, Arial, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
  display: flex;
  flex-direction: column;
  background-color: #f9fafb;
  color: #4b5563;
  border-right: 1px solid #f0f0f0;
  width: $sidebar-width-collapsed;
  overflow: hidden;
  height: 100vh;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  flex: 1 1 auto !important;

  // Logo zone
  .spm-sidebar__logo {
    height: $header-height;
    max-height: $header-height;
    margin-bottom: 0;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #f0f0f0;

    .spm-sidebar__logo-link {
      display: flex;
      align-items: center;
    }

    .spm-sidebar__logo-img--full {
      height: 30px;
      object-fit: contain;
    }

    .spm-sidebar__logo-img--mini {
      height: 28px;
      object-fit: contain;
    }
  }

  // Nav zone (scrollable only on hover)
  .spm-sidebar__nav {
    padding: 0.75rem 0.5rem;
    overflow: hidden;
    flex: 1;

    &::-webkit-scrollbar {
      width: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.08);
      border-radius: 3px;
    }
  }

  &:hover .spm-sidebar__nav {
    overflow-y: auto;
  }

  // Section label
  .spm-sidebar__section-label {
    opacity: 0;
    transition: opacity 0.15s ease;
    font-size: 0.625rem;
    letter-spacing: 0.08em;
    color: #9ca3af;
    font-weight: 600;
    margin-bottom: 0.5rem;
    margin-left: 0.5rem;
    text-transform: uppercase;
    display: block;
  }

  // Menu icon colors
  .spm-sidebar__menu {
    .menu-icon,
    .spm-panel-menu__icon {
      color: #9ca3af !important;
      transition: color 0.15s ease;
    }

    .menu-label,
    .spm-panel-menu__label {
      font-weight: 500;
    }

    .right-icon-item {
      color: #9ca3af !important;
    }
  }

  // Active indicator — green bar on left
  .spm-sidebar__menu .spm-panel-menu {
    .p-panelmenu-panel.activeMenuItem {
      .p-panelmenu-header .p-panelmenu-header-content {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 20%;
          height: 60%;
          width: 3px;
          background: $brand-color-primary;
          border-radius: 0 3px 3px 0;
        }
      }
    }

    // Sub-items nesting line
    .p-toggleable-content {
      .p-panelmenu-content {
        border-left: 2px solid #e5e7eb;
        margin-left: 1.25rem;
        padding-left: 0.25rem !important;

        .p-menuitem.activeMenuItem {
          .p-menuitem-content {
            position: relative;
          }
        }
      }

      // Active sub-item nesting line color
      &:has(.p-menuitem.activeMenuItem) {
        .p-panelmenu-content {
          border-left-color: $brand-color-primary;
        }
      }
    }
  }

  // --- EXPANDED state ---
  &.is-expanded {
    width: $sidebar-width-expanded;

    .spm-sidebar__section-label {
      opacity: 1;
    }

    .spm-sidebar__logo {
      padding: 0.75rem 1rem;
      justify-content: flex-start;
    }
  }

  // --- COLLAPSED state ---
  &:not(.is-expanded) {
    .spm-sidebar__nav {
      padding: 0.5rem 0.25rem;
    }

    .spm-sidebar__section-label {
      display: none;
    }

    .p-panelmenu-header {
      padding: 0.15rem;

      .p-panelmenu-header-content {
        & > div,
        & .spm-panel-menu__item {
          text-align: center;
          padding: 0.6rem 0.25rem !important;
          justify-content: center;
        }

        span.menu-label,
        .spm-panel-menu__label {
          display: none;
        }

        .menu-icon,
        .spm-panel-menu__icon {
          font-weight: 400 !important;
          margin-right: 0 !important;
          font-size: 1.15rem !important;
        }

        .right-icon-item,
        .spm-panel-menu__chevron {
          display: none;
        }
      }
    }
  }
}

// Popup menus for collapsed sidebar
#menuProducts, #menuLists, #menuContentData, #menuStats {
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e5e7eb;
  width: unset;
  min-width: 12rem !important;
  overflow: hidden;
  animation: spm-popup-enter 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  .p-menu-list {
    padding: 0.25rem;

    .p-menuitem {
      padding: 0.1rem 0;

      &.activeMenuItem {
        .p-menuitem-content {
          background-color: rgba(148, 200, 64, 0.08);

          .p-menuitem-link {
            color: #4a7a12 !important;
          }
        }
      }

      .p-menuitem-content {
        border-radius: 8px;
        font-size: 0.8125rem;
        font-weight: 500;
        color: #374151;
        transition: all 0.15s ease;

        &:hover {
          background: #f5f6f8;
        }
      }
    }
  }
}

@keyframes spm-popup-enter {
  from {
    opacity: 0;
    transform: translateX(-4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}
</style>
